import { CircleAlertIcon } from '@persona-id/design-tokens/icons';
import { DateTime } from 'luxon';
import { FC } from 'react';
import { Helmet } from 'react-helmet-async';

import InactiveEnvironmentBanner from 'dashboard/components/InactiveEnvironmentBanner';
import Banner from 'dashboard/components-lib/Banner';
import LoadingIndicator from 'dashboard/components-lib/LoadingIndicator';
import NullState from 'dashboard/components-lib/NullState';
import { FlagIssuesFormData } from 'dashboard/forms/FormFlagIssues';
import { FormUpdateTagsData } from 'dashboard/forms/FormUpdateTags';
import { Container, Section } from 'dashboard/layouts/IndexViewLayoutV2';
import { Backdrop } from 'dashboard/layouts/ShowViewLayoutV2';
import { userDisplayName } from 'dashboard/lib/valueDisplayers/userDisplayName';
import { RedactedObjectPath } from 'dashboard/models/event';
import { Inquiry } from 'dashboard/models/inquiry';
import { List } from 'dashboard/models/list';
import { ListItem } from 'dashboard/models/list-item';
import { ListMatch } from 'dashboard/models/list-match';
import { Verification } from 'dashboard/models/verification';
import { TransactionWithRelatedData } from 'dashboard/routes/DashboardTransactionsShowRoute/util';
import TransactionDetail from 'dashboard/views/DashboardTransactionsShowView/TransactionDetail';
import TransactionsShowHeader from 'dashboard/views/DashboardTransactionsShowView/TransactionsShowHeader';
import TransactionSummaryCard from 'dashboard/views/DashboardTransactionsShowView/TransactionSummaryCard';
import { DATE_AND_TIME_FORMAT } from 'lib/constants/luxonFormats';

interface DashboardTransactionsShowViewProps {
  cases: TransactionWithRelatedData['cases'];
  currentEnvironment: TransactionWithRelatedData['environment'];
  isLoading: boolean;
  nullStateMessage: string;
  redactedObjectPaths: RedactedObjectPath[];
  redactor?: TransactionWithRelatedData['redactor'];
  relatedAccounts: TransactionWithRelatedData['relatedAccounts'];
  relatedCases: TransactionWithRelatedData['relatedCases'];
  relatedInquiries: TransactionWithRelatedData['relatedInquiries'];
  relatedReports: TransactionWithRelatedData['relatedReports'];
  relatedTransactions: TransactionWithRelatedData['relatedTransactions'];
  relatedVerifications: TransactionWithRelatedData['relatedVerifications'];
  relations: TransactionWithRelatedData['relations'];
  transaction: TransactionWithRelatedData['transaction'];
  transactionLabel: TransactionWithRelatedData['transactionLabel'];
  transactionStatuses: TransactionWithRelatedData['transactionStatuses'];
  transactionTags: TransactionWithRelatedData['tags'];
  transactionType: TransactionWithRelatedData['transactionType'];
  lists: Record<string, List>;
  listItems: Record<string, ListItem>;
  listMatches: Record<string, ListMatch>;
  handleUpdateTags: (tags: FormUpdateTagsData) => void;
  handleUpdateTransactionStatus: (transactionStatusId: string) => Promise<void>;
  redactTransaction: (transactionId: string) => void;
  handleFlagInquiryIssues: (
    inquiry: Inquiry,
    verifications: Verification[],
    values: FlagIssuesFormData,
  ) => Promise<void>;
  handleFlagVerificationIssues: (
    verification: Verification,
    values: FlagIssuesFormData,
  ) => Promise<void>;
}

const DashboardTransactionsShowView: FC<DashboardTransactionsShowViewProps> = ({
  cases = [],
  currentEnvironment,
  isLoading,
  nullStateMessage,
  redactedObjectPaths = [],
  redactor,
  relatedAccounts,
  relatedCases,
  relatedInquiries,
  relatedReports,
  relatedTransactions,
  relatedVerifications,
  relations,
  transaction,
  transactionLabel,
  transactionStatuses,
  transactionTags,
  transactionType,
  lists,
  listItems,
  listMatches,
  handleUpdateTags,
  handleUpdateTransactionStatus,
  handleFlagInquiryIssues,
  handleFlagVerificationIssues,
  redactTransaction,
}) => {
  if (transaction == null || transactionStatuses == null) {
    return isLoading ? (
      <Backdrop>
        <LoadingIndicator fullscreen />
      </Backdrop>
    ) : (
      <Backdrop>
        <NullState message={nullStateMessage} backRoute='/dashboard/transactions' />
      </Backdrop>
    );
  }

  const redactorText = redactor == null ? 'via API' : `by ${userDisplayName(redactor)}`;
  const redactionBannerText = transaction.attributes['marked-for-cascading-redaction-at']
    ? `This transaction was marked for redaction ${redactorText} on ${DateTime.fromISO(
        transaction.attributes['marked-for-cascading-redaction-at'],
      ).toFormat(DATE_AND_TIME_FORMAT)}.`
    : `This transaction has been redacted ${redactorText} on ${DateTime.fromISO(
        transaction.attributes['redacted-at'],
      ).toFormat(DATE_AND_TIME_FORMAT)}.`;

  return (
    <>
      <Helmet title={`${transaction.id} - Transactions`} />

      <InactiveEnvironmentBanner />
      {transaction.attributes['redacted-at'] && (
        <Banner
          className='dashboard-transactions-show-view-redaction__redaction-banner'
          icon={<CircleAlertIcon />}
        >
          {redactionBannerText}
        </Banner>
      )}
      <TransactionsShowHeader
        transaction={transaction}
        transactionStatuses={transactionStatuses}
        transactionType={transactionType!}
        onConfirmRedaction={() => redactTransaction(transaction.id)}
        onSelectNewStatus={handleUpdateTransactionStatus}
      />
      <Container>
        <Section>
          <TransactionSummaryCard
            redactedObjectPaths={redactedObjectPaths}
            transaction={transaction}
            transactionLabel={transactionLabel}
            transactionStatuses={transactionStatuses}
            transactionType={transactionType!}
          />
          <TransactionDetail
            cases={cases}
            currentEnvironment={currentEnvironment!}
            redactedObjectPaths={redactedObjectPaths}
            relatedAccounts={relatedAccounts}
            relatedCases={relatedCases}
            relatedInquiries={relatedInquiries}
            relatedReports={relatedReports}
            relatedTransactions={relatedTransactions}
            relatedVerifications={relatedVerifications}
            relations={relations}
            transaction={transaction}
            transactionTags={transactionTags}
            lists={lists}
            listItems={listItems}
            listMatches={listMatches}
            handleUpdateTags={handleUpdateTags}
            handleFlagInquiryIssues={handleFlagInquiryIssues}
            handleFlagVerificationIssues={handleFlagVerificationIssues}
          />
        </Section>
      </Container>
    </>
  );
};

export default DashboardTransactionsShowView;
